import { Component, Input, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';

import { SocialTopicStatusEnum } from '../../enums/social-topic-status.enum';
import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { MediaSubscriptionStatusEnum } from '../../../../Monitor/Shared/enums/media-subscription-status.enum';
import { MediaSubscriptionService } from '../../../../Monitor/Core/services/media-subscription.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MonitoringQuotaTypesEnum } from '../../../../api/mentions/enum/monitoring-quota-types.enum';

@Component({
    selector: 'agpr-social-topic-status-switch',
    templateUrl: './social-topic-status-switch.component.html',
    styleUrls: ['./social-topic-status-switch.component.scss'],
    providers: [getProvider(SocialTopicStatusSwitchComponent)],
    standalone: false
})
export class SocialTopicStatusSwitchComponent extends ControlValueAccessorModel<MediaSubscriptionStatusEnum> implements OnDestroy {
    @Input() id: string;
    @Input() initiallyFocusable: boolean = true;
    @Input() ariaDescribedBy: string;
    @Input() disabled: boolean;
    @Input() agentName: string;
    @Input() isPartiallyModerated: boolean;
    @Input() partiallyModeratedTooltip: string;
    @Input() disabledTooltip: string;

    readonly mediaSubscriptionStatusEnum = MediaSubscriptionStatusEnum;
    isSwitchOn: boolean;
    switchDisabled: boolean;

    onText = `Social.status.${SocialTopicStatusEnum.active}`;
    offText: string;
    disabledStates: MediaSubscriptionStatusEnum[] = [
        MediaSubscriptionStatusEnum.STOPPED
    ];
    isSwitchOnStates: MediaSubscriptionStatusEnum[] = [
        MediaSubscriptionStatusEnum.ACTIVE,
        MediaSubscriptionStatusEnum.STAND_BY
    ];
    statusMapping: {[key: string]: any} = {
        [MediaSubscriptionStatusEnum.ACTIVE]: SocialTopicStatusEnum.active,
        [MediaSubscriptionStatusEnum.PAUSED]: SocialTopicStatusEnum.paused,
        [MediaSubscriptionStatusEnum.MODERATED]: SocialTopicStatusEnum.moderated,
        [MediaSubscriptionStatusEnum.STAND_BY]: SocialTopicStatusEnum.standby,
        [MediaSubscriptionStatusEnum.STOPPED]: SocialTopicStatusEnum.expired
    };
    isQuotaExceeded: boolean = false;
    socialTopicStatus: SocialTopicStatusEnum;

    private subscriptions: Subscription[] = [];

    constructor(
        private mediaSubscriptionService: MediaSubscriptionService,
        private localizationService: LocalizationService
    ) {
        super();
        this.subscriptions.push(
            this.mediaSubscriptionService.getIsQuotaExceeded(MonitoringQuotaTypesEnum.SOCIAL_LISTENING).subscribe(isQuotaExceeded => {
                this.isQuotaExceeded = isQuotaExceeded ?? false;
            })
        );
    }

    writeValue(value: MediaSubscriptionStatusEnum): void {
        this.value = value;
        this.updateSwitchStatus(this.value);
    }

    switchClick() {
        if (this.isSwitchOn) {
            if (this.value === MediaSubscriptionStatusEnum.PAUSED) {
                this.value = MediaSubscriptionStatusEnum.ACTIVE;
                if (this.isQuotaExceeded) {
                    this.value = MediaSubscriptionStatusEnum.STAND_BY;
                }
            } else if (this.value === MediaSubscriptionStatusEnum.MODERATED) {
                this.value = MediaSubscriptionStatusEnum.ACTIVE;
            }
        } else {
            if (this.value === MediaSubscriptionStatusEnum.ACTIVE || this.value === MediaSubscriptionStatusEnum.STAND_BY) {
                this.value = MediaSubscriptionStatusEnum.PAUSED;
            }
        }
        this.updateSwitchStatus(this.value);
        this.onModelChange(this.value);
    }

    updateSwitchStatus(status: MediaSubscriptionStatusEnum) {
        this.switchDisabled = this.disabledStates.includes(status);
        this.isSwitchOn = this.isSwitchOnStates.includes(status);
        this.socialTopicStatus = this.statusMapping[status];

        if (this.isQuotaExceeded) {
            if (status === MediaSubscriptionStatusEnum.PAUSED) {
                this.socialTopicStatus = SocialTopicStatusEnum.pausedStandby;
            } else if (status === MediaSubscriptionStatusEnum.STAND_BY || status === MediaSubscriptionStatusEnum.ACTIVE) {
                this.socialTopicStatus = SocialTopicStatusEnum.activeStandby;
            }
        } else {
            if (status === MediaSubscriptionStatusEnum.STAND_BY) {
                this.socialTopicStatus = SocialTopicStatusEnum.active;
            }
        }
        this.offText = this.localizationService.transform(`Social.status.${this.socialTopicStatus}`);

    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
